import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  CalendarDays,
  LayoutGrid,
  Medal,
  Table2,
  Trophy,
  Users,
} from "lucide-react";

function initials(name) {
  return String(name || "?")
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((p) => p[0])
    .join("")
    .toUpperCase();
}

function playerLabel(match) {
  const a = match.player_a_name || match.placeholder_a || "TBD";
  const b = match.player_b_name || match.placeholder_b || "TBD";
  return `${a} vs ${b}`;
}

function ScoreLine({ gamesToWin, busy, onSave, initialSets = null }) {
  const { t } = useTranslation();
  const [rows, setRows] = useState(
    initialSets?.length
      ? initialSets.map((s) => ({ a: String(s.a ?? ""), b: String(s.b ?? "") }))
      : [{ a: "", b: "" }]
  );
  useEffect(() => {
    if (!initialSets?.length) return;
    setRows(initialSets.map((s) => ({ a: String(s.a ?? ""), b: String(s.b ?? "") })));
  }, [initialSets]);
  return (
    <form
      className="tour-score"
      onSubmit={(e) => {
        e.preventDefault();
        const sets = rows
          .filter((r) => r.a !== "" && r.b !== "")
          .map((r) => ({ a: Number(r.a), b: Number(r.b) }));
        onSave?.(sets);
      }}
    >
      {rows.map((row, i) => (
        <span key={i} className="tour-score__game">
          <input
            inputMode="numeric"
            aria-label={t("tourGameA", "Game {{n}} points, player A", { n: i + 1 })}
            value={row.a}
            onChange={(e) =>
              setRows((prev) => prev.map((r, idx) => (idx === i ? { ...r, a: e.target.value } : r)))
            }
          />
          <input
            inputMode="numeric"
            aria-label={t("tourGameB", "Game {{n}} points, player B", { n: i + 1 })}
            value={row.b}
            onChange={(e) =>
              setRows((prev) => prev.map((r, idx) => (idx === i ? { ...r, b: e.target.value } : r)))
            }
          />
        </span>
      ))}
      {rows.length < gamesToWin * 2 - 1 ? (
        <button type="button" onClick={() => setRows((prev) => [...prev, { a: "", b: "" }])}>
          {t("tourAddGame", "Add game")}
        </button>
      ) : null}
      <button type="submit" disabled={busy}>
        {t("tourSaveScore", "Save score")}
      </button>
    </form>
  );
}

function fileToJpegBase64(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result || ""));
    reader.onerror = () => reject(new Error("Could not read that photo."));
    reader.readAsDataURL(file);
  });
}

function MatchScoreActions({
  match,
  canScore,
  canOpenLive,
  gamesToWin,
  busy,
  onScore,
  onOpenLive,
  onScorePhoto,
}) {
  const { t } = useTranslation();
  const [draft, setDraft] = useState(null);
  const [photoBusy, setPhotoBusy] = useState(false);
  const [photoError, setPhotoError] = useState("");
  const liveBusy = match.championship_status === "live" || match.championship_status === "lineup";
  const showTyped = canScore && !liveBusy;

  const onPhoto = async (file) => {
    if (!file || !onScorePhoto) return;
    setPhotoBusy(true);
    setPhotoError("");
    try {
      const dataUrl = await fileToJpegBase64(file);
      const result = await onScorePhoto(match.match_id, dataUrl);
      setDraft(result?.sets || null);
      if (!result?.sets?.length) {
        setPhotoError(result?.notes || t("tourPhotoNoScore", "No game scores found on the photo."));
      }
    } catch (err) {
      setPhotoError(err?.response?.data?.message || err.message);
    } finally {
      setPhotoBusy(false);
    }
  };

  return (
    <div className="tour-score-actions">
      {canOpenLive ? (
        <button
          type="button"
          className="tour-score-actions__live"
          disabled={busy}
          onClick={() => onOpenLive?.(match.match_id)}
        >
          {t("tourScoreLive", "Score live")}
        </button>
      ) : null}
      {canScore && onScorePhoto ? (
        <label className={`tour-score-actions__photo${photoBusy ? " is-busy" : ""}`}>
          <input
            type="file"
            accept="image/*"
            capture="environment"
            disabled={busy || photoBusy}
            onChange={(e) => {
              const file = e.target.files?.[0];
              e.target.value = "";
              onPhoto(file);
            }}
          />
          {photoBusy
            ? t("tourPhotoReading", "Reading the scorecard…")
            : t("tourPhotoPaper", "Photo of paper")}
        </label>
      ) : null}
      {photoError ? <p className="tour-score-actions__err">{photoError}</p> : null}
      {draft?.length ? (
        <p className="tour-score-actions__hint">
          {t("tourPhotoConfirm", "Check the games below, then save.")}
        </p>
      ) : null}
      {showTyped ? (
        <ScoreLine
          gamesToWin={gamesToWin}
          busy={busy || photoBusy}
          initialSets={draft}
          onSave={(sets) => {
            setDraft(null);
            onScore?.(match.match_id, sets);
          }}
        />
      ) : null}
      {liveBusy && canScore ? (
        <p className="tour-score-actions__hint">
          {t("tourLiveInProgress", "Live scoring is open for this match.")}
        </p>
      ) : null}
    </div>
  );
}

export default function TournamentScheduleView({
  view,
  preview,
  categories = [],
  players = [],
  currentUserId = null,
  canNotify = false,
  onNotify,
  busy = false,
  canScore = false,
  canOpenLive = false,
  gamesToWin = 3,
  onScore,
  onOpenLive,
  onScorePhoto,
}) {
  const { t } = useTranslation();
  const [picked, setPicked] = useState([]);
  const cats = view?.categories || categories;
  const people = view?.players || players;
  const pools = view?.pools || [];
  const matches = view?.matches || preview?.matches || [];
  const previewPools = preview?.categories || [];
  const byTable = view?.schedule?.by_table;
  const bySlot = view?.schedule?.by_slot;
  const headline = view?.generated_meta?.headline || preview?.headline;
  const warnings = view?.generated_meta?.warnings || preview?.warnings || [];
  const me = people.find((p) => String(p.user_id) === String(currentUserId));

  useEffect(() => {
    if (me?.category_ids?.length) setPicked(me.category_ids);
    else if (cats[0]?.category_id || cats[0]?.id) {
      setPicked([cats[0].category_id || cats[0].id]);
    }
  }, [me, cats]);

  const tableGroups = useMemo(() => {
    if (byTable?.length) return byTable;
    const map = {};
    matches.forEach((m) => {
      if (m.table_number == null) return;
      const key = String(m.table_number);
      map[key] = map[key] || [];
      map[key].push(m);
    });
    return Object.keys(map)
      .sort((a, b) => Number(a) - Number(b))
      .map((n) => ({ table_number: Number(n), matches: map[n] }));
  }, [byTable, matches]);

  const slotGroups = useMemo(() => {
    if (bySlot?.length) return bySlot;
    const map = {};
    matches.forEach((m) => {
      if (m.slot_index == null) return;
      const key = String(m.slot_index);
      map[key] = map[key] || [];
      map[key].push(m);
    });
    return Object.keys(map)
      .sort((a, b) => Number(a) - Number(b))
      .map((n) => ({ slot_index: Number(n), matches: map[n] }));
  }, [bySlot, matches]);

  const nameOf = (id) => {
    const entry = (view?.entries || []).find((e) => e.entry_id === id);
    const personId = entry?.tournament_player_id || id;
    const p = people.find(
      (x) => x.tournament_player_id === personId || x.id === personId
    );
    return p?.name || null;
  };

  const toggleCat = (id) => {
    setPicked((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  return (
    <div className="tour-view">
      {headline ? <p className="tour-view__headline">{headline}</p> : null}
      {warnings.map((w) => (
        <p key={w} className="tour-view__warn">
          {w}
        </p>
      ))}

      {canNotify ? (
        <section className="tour-view__presence">
          <h3>{t("tourNotifyTitle", "Are you playing?")}</h3>
          <p>
            {t(
              "tourNotifyLede",
              "Tell the club you will be there, and pick the categories you want to enter."
            )}
          </p>
          <div className="tour-view__chips">
            {cats.map((c) => {
              const id = c.category_id || c.id;
              return (
                <button
                  key={id}
                  type="button"
                  className={picked.includes(id) ? "is-on" : ""}
                  onClick={() => toggleCat(id)}
                >
                  {c.name}
                </button>
              );
            })}
          </div>
          <div className="tour-view__presence-acts">
            <button
              type="button"
              className="is-in"
              disabled={busy || !picked.length}
              onClick={() => onNotify?.("notified", picked)}
            >
              {t("tourNotifyYes", "I'll be there")}
            </button>
            <button
              type="button"
              className="is-out"
              disabled={busy}
              onClick={() => onNotify?.("declined", [])}
            >
              {t("tourNotifyNo", "I can't play")}
            </button>
          </div>
          {me?.presence && me.presence !== "declined" ? (
            <p className="tour-view__hint">
              {t("tourNotifySaved", "Presence saved")} · {me.presence}
            </p>
          ) : null}
        </section>
      ) : null}

      <section>
        <h3>
          <Users size={16} aria-hidden /> {t("tourPools", "Pools")}
        </h3>
        <div className="tour-view__pools">
          {(pools.length ? pools : previewPools.flatMap((c) =>
            (c.pools || []).map((p) => ({ ...p, category_id: c.id, category_name: c.name }))
          )).map((pool) => (
            <article key={`${pool.category_id || ""}-${pool.letter}`}>
              <header>
                <strong>
                  {t("tourPool", "Pool")} {pool.letter}
                </strong>
                <em>
                  {pool.category_name ||
                    cats.find((c) => (c.category_id || c.id) === pool.category_id)?.name ||
                    ""}
                </em>
              </header>
              <ol>
                {(pool.players || []).map((p) => (
                  <li key={p.tournament_player_id || p.id}>
                    <span className="tour-ava">{initials(p.name)}</span>
                    <span>
                      <strong>{p.name}</strong>
                      <em>
                        {[p.club_name, p.ranking_points != null ? `${p.ranking_points} pts` : null]
                          .filter(Boolean)
                          .join(" · ")}
                      </em>
                    </span>
                  </li>
                ))}
                {!pool.players?.length && (pool.player_ids || []).map((id) => (
                  <li key={id}>
                    <span className="tour-ava">{initials(nameOf(id))}</span>
                    <span>
                      <strong>{nameOf(id) || id}</strong>
                    </span>
                  </li>
                ))}
              </ol>
            </article>
          ))}
        </div>
      </section>

      <section>
        <h3>
          <Table2 size={16} aria-hidden /> {t("tourByTable", "Schedule by table")}
        </h3>
        <div className="tour-view__tables">
          {tableGroups.map((g) => (
            <article key={g.table_number}>
              <header>
                {t("tourTable", "Table")} {g.table_number}
              </header>
              <ul>
                {g.matches.map((m, i) => (
                  <li key={m.match_id || m.temp_id || i}>
                    <span className="tour-view__slot">R{(m.slot_index || 0) + 1}</span>
                    <span>
                      <strong>{playerLabel({ ...m, player_a_name: m.player_a_name || nameOf(m.player_a_id), player_b_name: m.player_b_name || nameOf(m.player_b_id) })}</strong>
                      <em>
                        {m.round_label}
                        {m.stage !== "pool" ? ` · ${String(m.stage || "").replace("_", " ")}` : ""}
                        {m.status === "completed" || m.status === "bye"
                          ? ` · ${m.score_a ?? ""}–${m.score_b ?? ""}`
                          : ""}
                      </em>
                      {m.status === "scheduled" && m.entry_a_id && m.entry_b_id ? (
                        <MatchScoreActions
                          match={m}
                          canScore={canScore}
                          canOpenLive={
                            canOpenLive ||
                            canScore ||
                            (currentUserId &&
                              (String(m.entry_a_user_id) === String(currentUserId) ||
                                String(m.entry_b_user_id) === String(currentUserId)))
                          }
                          gamesToWin={gamesToWin}
                          busy={busy}
                          onScore={onScore}
                          onOpenLive={onOpenLive}
                          onScorePhoto={onScorePhoto}
                        />
                      ) : null}
                    </span>
                  </li>
                ))}
              </ul>
            </article>
          ))}
        </div>
      </section>

      {view?.standings ? (
        <section>
          <h3>
            <Medal size={16} aria-hidden /> {t("tourStandings", "Standings")}
          </h3>
          {Object.entries(view.standings).map(([catId, block]) => (
            <div key={catId}>
              {(block.pools || []).map((pool) => (
                <ol key={pool.letter}>
                  {(pool.rows || []).map((row) => (
                    <li key={row.id}>
                      {nameOf(row.id) || row.id} · {row.wins} {t("tourWins", "wins")}
                    </li>
                  ))}
                </ol>
              ))}
              {(block.swiss || []).length ? (
                <ol>
                  {block.swiss.map((row, i) => (
                    <li key={row.id}>
                      {i + 1}. {nameOf(row.id) || row.id} · {row.points} pts · Buchholz {row.buchholz}
                    </li>
                  ))}
                </ol>
              ) : null}
            </div>
          ))}
        </section>
      ) : null}

      <section>
        <h3>
          <Trophy size={16} aria-hidden /> {t("tourFinals", "Finals")}
        </h3>
        <ul className="tour-view__finals">
          {matches
            .filter((m) => m.stage !== "pool")
            .map((m, i) => (
              <li key={m.match_id || m.temp_id || i}>
                <Medal size={14} aria-hidden />
                <span>
                  <strong>
                    {playerLabel({
                      ...m,
                      player_a_name: m.player_a_name || nameOf(m.player_a_id),
                      player_b_name: m.player_b_name || nameOf(m.player_b_id),
                    })}
                  </strong>
                  <em>
                    {m.round_label}
                    {m.table_number ? ` · ${t("tourTable", "Table")} ${m.table_number}` : ""}
                  </em>
                </span>
              </li>
            ))}
        </ul>
      </section>

      {slotGroups.length ? (
        <p className="tour-view__hint">
          <CalendarDays size={14} aria-hidden />{" "}
          {t("tourSlotsHint", "{{n}} parallel rounds on the assigned tables", {
            n: slotGroups.length,
          })}
        </p>
      ) : (
        <p className="tour-view__hint">
          <LayoutGrid size={14} aria-hidden />{" "}
          {t("tourNoMatchesYet", "Add players to see the draw.")}
        </p>
      )}
    </div>
  );
}
